import * as React from "react";
import { pure } from "recompose";
import styled from "styled-components";

import {
  Card,
  IconLocation,
  IconPerson,
  OrderSummarySection,
  Text,
} from "../../../../components/atoms";
import {
  IOrderSummaryButtonProps,
  IOrderSummaryTotalProps,
  IOrderSummaryTreatmentsProps,
  OrderSummaryAddress,
  OrderSummaryButton,
  OrderSummaryTime,
  OrderSummaryTotal,
  OrderSummaryTreatments,
} from "../../../../components/molecules";
import { BookingLocationPresenter } from "../../../../presenters/booking-location";
import { ITherapist } from "../../../../services/api/therapists";
import { AddressType } from "../../../../store/postcodes/reducer";
import { theme } from "../../../../theme";
import { useBookingFee } from "./use-booking-fee";
import { useEffect } from "react";

interface IProps
  extends IOrderSummaryButtonProps,
    IOrderSummaryTotalProps,
    IOrderSummaryTreatmentsProps {
  bookingLocation: BookingLocationPresenter;
  bookingAddressString?: string;
  billingLocation: BookingLocationPresenter;
  billingAddressString?: string;
  date: string;
  therapist: ITherapist;
  isLoggedIn: boolean;
  isBookingFeeLoading: boolean;
  changeAddress: (addressType: AddressType) => void;
}

const OrderSummaryHeader = styled(Text)`
  padding-bottom: 12px;
  border-bottom: 1px solid ${theme.color.inputBorderColor};
`;

const OrderSummaryCard = styled(Card)`
  height: auto;
  padding-left: 20px;
  padding-top: 20px;
  padding-right: 20px;
`;

const OrderSummaryDetailsSection = styled(OrderSummarySection)`
  display: flex;
  flex-direction: column;

  > *:not(:last-child) {
    padding-bottom: 10px;
  }

  &:last-child {
    border: 0;
  }
`;

const OrderSummaryDetails = styled.div`
  display: inline-flex;

  > svg {
    margin-right: 10px;
    width: 14px;

    path {
      fill: ${theme.color.ruubyGreyText};
    }
  }
`;

const OrderSummary = (props: IProps): JSX.Element => {
  const { requestBookingFee } = useBookingFee();

  useEffect(() => {
    if (props.treatments.length > 0 && props.date) {
      requestBookingFee(
        new Date(props.date),
        props.therapist.urn,
        props.treatments,
      );
    }
  }, [props.originalAmount, props.date]);

  const renderBookingAddress = () => {
    if (
      props.isLoggedIn ||
      (props.bookingLocation &&
        props.bookingLocation.canCheckoutWithThisAddress())
    ) {
      return (
        <OrderSummarySection>
          <OrderSummaryAddress
            addressType={AddressType.BOOKING_ADDRESS}
            address={props.bookingAddressString}
            isLoggedIn={props.isLoggedIn}
            changeAddress={props.changeAddress}
          />
        </OrderSummarySection>
      );
    }
  };

  const renderBillingAddress = () => {
    const billingLocation = props.billingLocation;
    if (
      props.isLoggedIn &&
      billingLocation &&
      billingLocation.canCheckoutWithThisAddress()
    ) {
      return (
        <OrderSummarySection>
          <OrderSummaryAddress
            addressType={AddressType.BILLING_ADDRESS}
            address={props.billingAddressString}
            isLoggedIn={props.isLoggedIn}
            changeAddress={props.changeAddress}
          />
        </OrderSummarySection>
      );
    }
  };

  return (
    <OrderSummaryCard>
      <OrderSummaryHeader size="14" weight="bold" transform="uppercase">
        Appointment Details
      </OrderSummaryHeader>
      <OrderSummaryDetailsSection>
        {props.date && (
          <OrderSummaryTime date={props.date} treatments={props.treatments} />
        )}
        {props.bookingAddressString && (
          <OrderSummaryDetails>
            <IconLocation />
            <Text size="14">{props.bookingAddressString}</Text>
          </OrderSummaryDetails>
        )}
        <OrderSummaryDetails>
          <IconPerson />
          <Text size="14">{props.therapist.therapistName}</Text>
        </OrderSummaryDetails>
      </OrderSummaryDetailsSection>
      {renderBookingAddress()}
      {renderBillingAddress()}
      {props.treatments.length > 0 && (
        <React.Fragment>
          <OrderSummaryTreatments
            treatments={props.treatments}
            onAdd={props.onAdd}
            onRemove={props.onRemove}
          />
          <OrderSummaryTotal
            creditBalance={props.creditBalance}
            creditSubBalance={props.creditSubBalance}
            originalAmount={props.originalAmount}
            promocode={props.promocode}
            removePromo={props.removePromo}
            bookingFee={props.bookingFee}
            isBookingFeeLoading={props.isBookingFeeLoading}
            totalAmount={props.totalAmount}
          />
        </React.Fragment>
      )}
      {props.onCheckout && props.treatments.length > 0 ? (
        <OrderSummaryButton
          isCheckLoading={props.isCheckLoading || props.isBookingFeeLoading}
          onCheckout={props.onCheckout}
        />
      ) : undefined}
    </OrderSummaryCard>
  );
};

const pureComponent = pure(OrderSummary);

export { pureComponent as OrderSummary };
